<script>
import IconButton from "@/components/shared/IconButton.vue";
import MarkdownEditor from "@/components/shared/MarkdownEditor.vue";
import { del, post, put } from "@/requests/server";
import dayjs from "dayjs";
import Vue from "vue";
import SafeFile from "@/components/shared/SafeFile.vue";
import SafeImage from "@/components/shared/SafeImage.vue";
import { isGlobalAdmin } from "@/helpers/permissions/users";

export default Vue.extend({
  name: "CommunityUserDetails",
  components: { IconButton, MarkdownEditor, SafeImage, SafeFile },
  props: {
    defaultEmailTemplate: {
      type: String,
      required: true,
    },
    communityUser: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      approvalNote: null,
      show: false,
      suspending: false,
      unsuspending: false,
      deleting: false,
      approving: false,
      evaluatingProof: false,
      changingSubscription: false,
      updatingVolunteerTag: false,
      incorrectProofEmailMarkdown: "",
      showIncorrectProofEmailModal: false,
      sendingIncorrectProofEmail: false,
      grantReason: this.communityUser.current_granted_subscription?.reason,
      subscriptionSectionVisible: false,
    };
  },
  computed: {
    requiredProofs() {
      // The order here is the display order.
      return {
        identity: this.communityUser.community.requires_identity_proof,
        residency: this.communityUser.community.requires_residency_proof,
        custom: this.communityUser.community.requires_custom_proof,
      };
    },
    submittedProofs() {
      return {
        identity: this.communityUser.user.identity_proof,
        residency: this.communityUser.user.residency_proof,
        custom: this.communityUser.custom_proof,
      };
    },
    visibleProofs() {
      // We assume that if the user has access to this component and they are
      // not global admin, then they are admin of the community.
      const globalAdmin = isGlobalAdmin(this.$store.state.user);

      return {
        identity: globalAdmin || !this.communityUser.approved_at,
        residency: globalAdmin || !this.communityUser.approved_at,
        custom: true,
      };
    },
    disabled() {
      return (
        this.suspending ||
        this.approving ||
        this.deleting ||
        this.unsuspending ||
        this.evaluatingProof ||
        this.changingSubscription ||
        this.updatingVolunteerTag
      );
    },
    isVolunteer() {
      return this.communityUser.tags?.includes("volunteer") ?? false;
    },
    volunteerTagValue() {
      return this.isVolunteer ? "volunteer" : null;
    },
    tagOptions() {
      return [
        {
          value: null,
          text: this.$t("components.community.community_user_details.tags.options.none"),
        },
        {
          value: "volunteer",
          text: this.$t("components.community.community_user_details.tags.options.volunteer"),
        },
      ];
    },
    subscriptionExpiresInLessThanAMonth() {
      return (
        !this.communityUser.current_granted_subscription ||
        dayjs(this.communityUser.current_granted_subscription.end_date)
          .startOf("day")
          .diff(dayjs().startOf("day"), "months") < 1
      );
    },
    subscriptionExpiresInLessThanAYear() {
      return (
        !this.communityUser.current_granted_subscription ||
        dayjs(this.communityUser.current_granted_subscription.end_date)
          .startOf("day")
          .diff(dayjs().startOf("day"), "years") < 1
      );
    },
    daysSinceStartOfSubscription() {
      return this.communityUser.current_granted_subscription
        ? dayjs()
            .startOf("day")
            .diff(this.communityUser.current_granted_subscription.start_date, "days")
        : 0;
    },
    userRoles() {
      return this.communityUser.roles;
    },
  },
  mounted() {
    this.$nextTick(() => (this.show = true));
  },
  methods: {
    userRoleBadgeVariant(role) {
      switch (role) {
        case "admin":
          return "danger";
        default:
          return "info";
      }
    },
    async updateVolunteerTag(value) {
      const shouldBeVolunteer = value === "volunteer";

      if (shouldBeVolunteer === this.isVolunteer) {
        return;
      }

      this.updatingVolunteerTag = true;

      const options = {
        cleanupCallback: () => (this.updatingVolunteerTag = false),
        notifications: {
          action: shouldBeVolunteer
            ? this.$t("components.community.community_user_details.notifications.volunteer_tag.add")
            : this.$t(
                "components.community.community_user_details.notifications.volunteer_tag.remove"
              ),
          onSuccess: true,
        },
      };

      const { data } = shouldBeVolunteer
        ? await post(
            `communityUsers/${this.communityUser.id}/tags`,
            {
              tag: "volunteer",
            },
            options
          )
        : await del(`communityUsers/${this.communityUser.id}/tags/volunteer`, options);

      Object.assign(this.communityUser, data);
    },
    async grantMonthSubscription() {
      const today = dayjs().startOf("day");
      await this.grantSubscription(
        today.add(1, "month").diff(today, "days") + this.daysSinceStartOfSubscription
      );
    },
    async grantYearSubscription() {
      const today = dayjs().startOf("day");
      await this.grantSubscription(
        today.add(1, "year").diff(today, "days") + this.daysSinceStartOfSubscription
      );
    },
    async grantSubscription(duration) {
      this.changingSubscription = true;
      const { data } = await put(
        "subscriptions/grant",
        {
          user_id: this.communityUser.user.id,
          community_id: this.communityUser.community.id,
          duration_in_days: duration,
          reason: this.grantReason,
        },
        {
          cleanupCallback: () => (this.changingSubscription = false),
          notifications: {
            action: this.$t(
              "components.community.community_user_details.notifications.grant_subscription.action"
            ),
            onSuccess: this.$t(
              "components.community.community_user_details.notifications.grant_subscription.success"
            ),
          },
        }
      );
      Object.assign(this.communityUser, { current_granted_subscription: data });
    },
    async revokeSubscription() {
      this.changingSubscription = true;
      await del(`subscriptions/${this.communityUser.current_granted_subscription.id}`, {
        cleanupCallback: () => (this.changingSubscription = false),
        notifications: {
          action: this.$t(
            "components.community.community_user_details.notifications.revoke_subscription.action"
          ),
          onSuccess: this.$t(
            "components.community.community_user_details.notifications.revoke_subscription.success"
          ),
        },
      });
      this.grantReason = null;
      Object.assign(this.communityUser, { current_granted_subscription: null });
    },
    async approveUser() {
      this.approving = true;
      const { data } = await put(
        `communityUsers/${this.communityUser.id}/approve`,
        {
          note: this.approvalNote,
        },
        {
          cleanupCallback: () => (this.approving = false),
          notifications: {
            action: this.$t(
              "components.community.community_user_details.notifications.approve.action"
            ),
          },
        }
      );
      Object.assign(this.communityUser, data);
    },
    async suspendUser() {
      this.suspending = true;

      const { data } = await put(`communityUsers/${this.communityUser.id}/suspend`, null, {
        cleanupCallback: () => (this.suspending = false),
        notifications: {
          action: this.$t(
            "components.community.community_user_details.notifications.suspend.action"
          ),
          onSuccess: true,
        },
      });

      Object.assign(this.communityUser, data);
    },
    async unsuspendUser() {
      this.unsuspending = true;

      const { data } = await put(`communityUsers/${this.communityUser.id}/unsuspend`, null, {
        cleanupCallback: () => (this.unsuspending = false),
        notifications: {
          action: this.$t(
            "components.community.community_user_details.notifications.unsuspend.action"
          ),
          onSuccess: true,
        },
      });

      Object.assign(this.communityUser, data);
    },
    async unEvaluateProof() {
      await this.evaluateProof("unevaluated");
    },
    invalidateProof() {
      this.incorrectProofEmailMarkdown =
        this.communityUser.community.incorrect_proof_email_markdown || "";
      this.showIncorrectProofEmailModal = true;
    },
    async invalidateProofWithoutEmail() {
      this.showIncorrectProofEmailModal = false;
      await this.evaluateProof("invalid");
    },
    async invalidateProofAndSendEmail() {
      this.sendingIncorrectProofEmail = true;

      try {
        await this.evaluateProof("invalid", {
          send_incorrect_proof_email: true,
          incorrect_proof_email_markdown: this.incorrectProofEmailMarkdown,
        });

        this.showIncorrectProofEmailModal = false;
      } finally {
        this.sendingIncorrectProofEmail = false;
      }
    },
    async evaluateProof(evaluation, payload = {}) {
      this.evaluatingProof = true;

      const { data } = await put(
        `communityUsers/${this.communityUser.id}`,
        {
          proof_evaluation: evaluation,
          ...payload,
        },
        {
          cleanupCallback: () => (this.evaluatingProof = false),
          notifications: {
            action: this.$t(
              "components.community.community_user_details.notifications.evaluate_proof.action"
            ),
          },
        }
      );

      Object.assign(this.communityUser, data);
    },
    async removeUser() {
      this.deleting = true;
      try {
        await this.$store.dispatch(`communities/removeUser`, {
          id: this.communityUser.community.id,
          userId: this.communityUser.user_id,
        });
      } finally {
        this.deleting = false;
      }

      this.$emit("deleted");
    },
  },
});
</script>

<template>
  <div class="user-details-row community-user-details">
    <b-row>
      <b-col sm="4">
        <dl>
          <dt>
            <span class="sr-only">{{
              $t("components.community.community_user_details.photo")
            }}</span>
          </dt>
          <dd v-if="communityUser.user.avatar">
            <safe-image
              :image="communityUser.user.avatar"
              aspect-ratio="1 / 1"
              class="w-auto user-details-image"
            />
          </dd>
          <dd v-else>{{ $t("components.community.community_user_details.no_photo") }}</dd>
        </dl>
      </b-col>
      <b-col sm="8">
        <div class="form__section">
          <h3>
            {{ $t("components.community.community_user_details.titles.user_details") | capitalize }}
          </h3>
          <dl>
            <dt>{{ $t("components.community.community_user_details.email") }}</dt>
            <dd>{{ communityUser.user.email }}</dd>
            <dt>{{ $t("components.community.community_user_details.description") }}</dt>
            <dd>{{ communityUser.user.description }}</dd>
            <dt>{{ $t("components.community.community_user_details.address") }}</dt>
            <dd>{{ communityUser.user.address }}</dd>
          </dl>
        </div>

        <div class="form__section">
          <h3>
            {{ $t("components.community.community_user_details.titles.approval") | capitalize }}
          </h3>

          <div class="mb-3">
            <b-badge v-if="communityUser.proof_state === 'absent'" variant="danger">
              {{ $t("components.community.community_user_details.proofs.states.absent") }}
            </b-badge>
            <b-badge v-if="communityUser.proof_state === 'partial'" variant="warning">
              {{ $t("components.community.community_user_details.proofs.states.partial") }}
            </b-badge>
            <b-badge v-else-if="communityUser.proof_state === 'unevaluated'" variant="warning">
              {{ $t("components.community.community_user_details.proofs.states.unevaluated") }}
            </b-badge>
            <b-badge v-else-if="communityUser.proof_state === 'valid'" variant="success">
              {{ $t("components.community.community_user_details.proofs.states.valid") }}
            </b-badge>
            <b-badge v-else-if="communityUser.proof_state === 'invalid'" variant="danger">
              {{ $t("components.community.community_user_details.proofs.states.invalid") }}
            </b-badge>
          </div>

          <!-- Display proof list if there is at least one required proof. -->
          <dl v-if="Object.keys(requiredProofs).find((key) => requiredProofs[key])" class="mb-3">
            <template
              v-for="proof_type in Object.keys(requiredProofs).filter((key) => requiredProofs[key])"
            >
              <!-- Custom proofs have a specific name -->
              <dt v-if="proof_type === 'custom'" :key="`dt_${proof_type}`">
                {{ communityUser.community.custom_proof_name }}
              </dt>
              <dt v-else :key="`dt_${proof_type}`">
                {{ $t(`components.community.community_user_details.proofs.${proof_type}`) }}
              </dt>

              <dd :key="`dd_${proof_type}`">
                <div v-if="!visibleProofs[proof_type]" class="text-muted">
                  {{ $t("components.community.community_user_details.proofs.hidden") }}
                </div>
                <div
                  v-else-if="
                    !submittedProofs[proof_type] || submittedProofs[proof_type].length === 0
                  "
                  class="text-muted"
                >
                  {{ $t("components.community.community_user_details.proofs.none_provided") }}
                </div>
                <ul v-else>
                  <li v-for="proof in submittedProofs[proof_type]" :key="proof.id">
                    <safe-file :file="proof" />
                  </li>
                </ul>
              </dd>
            </template>
          </dl>

          <icon-button
            v-if="communityUser.proof_state === 'unevaluated'"
            v-b-tooltip.hover="
              $t('components.community.community_user_details.proof_actions.invalidate.tooltip')
            "
            size="sm"
            :disabled="disabled"
            variant="ghost-danger"
            :onclick="invalidateProof"
          >
            {{ $t("components.community.community_user_details.proof_actions.invalidate.label") }}
          </icon-button>

          <b-modal
            v-model="showIncorrectProofEmailModal"
            title="Courriel de preuves insuffisantes"
            size="lg"
            hide-footer
          >
            <b-form-group
              label="Courriel à envoyer"
              description="Vous pouvez modifier le contenu avant l'envoi."
            >
              <markdown-editor
                v-model="incorrectProofEmailMarkdown"
                :placeholder="defaultEmailTemplate"
                :show-preview-initially="!!incorrectProofEmailMarkdown"
              />
            </b-form-group>

            <div class="d-flex justify-content-end">
              <icon-button
                variant="ghost-secondary"
                :disabled="sendingIncorrectProofEmail || evaluatingProof"
                :onclick="invalidateProofWithoutEmail"
              >
                Ne pas envoyer
              </icon-button>

              <icon-button
                class="ml-2"
                role="send"
                :disabled="sendingIncorrectProofEmail || evaluatingProof"
                :loading="sendingIncorrectProofEmail"
                :onclick="invalidateProofAndSendEmail"
              >
                Envoyer
              </icon-button>
            </div>
          </b-modal>

          <icon-button
            v-if="communityUser.proof_state === 'invalid'"
            v-b-tooltip.hover="
              $t('components.community.community_user_details.proof_actions.reset.tooltip')
            "
            size="sm"
            :disabled="disabled"
            variant="ghost-secondary"
            :onclick="unEvaluateProof"
          >
            {{ $t("components.community.community_user_details.proof_actions.reset.label") }}
          </icon-button>

          <b-form-group
            v-if="!communityUser.approved_at"
            size="sm"
            class="mb-2"
            :label="
              $t('components.community.community_user_details.subscription.approval.note.label')
            "
          >
            <b-textarea
              v-model="approvalNote"
              :rows="1"
              :max-rows="3"
              size="sm"
              :placeholder="
                $t(
                  'components.community.community_user_details.subscription.approval.note.placeholder'
                )
              "
            />
          </b-form-group>

          <dl class="mb-2">
            <template v-if="communityUser.approver && !communityUser.suspended_at">
              <dt>
                {{ $t("communities.fields.user.approver") | capitalize }}
              </dt>
              <dd>
                <b-link :to="`/admin/users/${communityUser.approver.id}`">
                  {{ communityUser.approver.full_name }}
                </b-link>
              </dd>
            </template>

            <template v-if="communityUser.approved_at && !communityUser.suspended_at">
              <dt>
                {{ $t("communities.fields.user.approved_at") | capitalize }}
              </dt>
              <dd>
                {{ communityUser.approved_at | date }}
              </dd>
            </template>

            <template v-else-if="communityUser.suspended_at">
              <dt>
                {{ $t("communities.fields.user.suspended_at") | capitalize }}
              </dt>
              <dd>
                {{ communityUser.suspended_at | date }}
              </dd>
            </template>

            <template v-else>
              <dt>
                {{ $t("communities.fields.user.created_at") | capitalize }}
              </dt>
              <dd>
                {{ communityUser.created_at | date }}
              </dd>
            </template>

            <template v-if="communityUser.approval_note">
              <dt>
                {{
                  $t("components.community.community_user_details.subscription.approval.note_label")
                }}
              </dt>
              <dd>{{ communityUser.approval_note }}</dd>
            </template>
          </dl>

          <div class="user-details-buttons">
            <icon-button
              v-if="!communityUser.approved_at && !communityUser.suspended_at"
              size="sm"
              variant="success"
              :onclick="approveUser"
              :disabled="disabled"
              icon="check2-square"
            >
              {{ $t("communities.fields.user.action_labels.approve") | capitalize }}
            </icon-button>

            <icon-button
              v-if="communityUser.approved_at && !communityUser.suspended_at"
              variant="outline-warning"
              size="sm"
              :onclick="suspendUser"
              :disabled="disabled"
              icon="pause-circle"
            >
              {{ $t("communities.fields.user.action_labels.suspend") | capitalize }}
            </icon-button>

            <icon-button
              v-if="communityUser.suspended_at"
              variant="success"
              size="sm"
              :onclick="unsuspendUser"
              :disabled="disabled"
              icon="play-circle"
            >
              {{ $t("communities.fields.user.action_labels.unsuspend") | capitalize }}
            </icon-button>

            <icon-button role="delete" size="sm" :onclick="removeUser" :disabled="disabled">
              {{ $t("communities.fields.user.action_labels.remove") | capitalize }}
            </icon-button>
          </div>
        </div>

        <div v-if="communityUser.approved_at && !communityUser.suspended_at" class="form__section">
          <h3>
            {{ $t("components.community.community_user_details.titles.subscription") | capitalize }}
          </h3>

          <div class="position-relative">
            <dl>
              <dt @click="subscriptionSectionVisible = !subscriptionSectionVisible">
                {{ $t("components.community.community_user_details.subscription.status") }}
                <b-icon
                  icon="caret-right-fill"
                  class="collapse-icon"
                  :class="{
                    'collapse-icon-open': subscriptionSectionVisible,
                  }"
                />
                <b-icon
                  v-b-tooltip.hover="
                    $t('components.community.community_user_details.subscription.info_tooltip')
                  "
                  class="text-muted info-icon"
                  icon="info-circle"
                  scale="0.8"
                />
              </dt>
              <dd>
                <span v-if="communityUser.current_granted_subscription">
                  <i18n path="components.community.community_user_details.subscription.free_until">
                    <template #date>
                      {{ communityUser.current_granted_subscription.end_date | date }}
                    </template>
                    <template #name>
                      <b-link
                        :to="`/admin/users/${communityUser.current_granted_subscription.granted_by_user.id}`"
                      >
                        {{
                          communityUser.current_granted_subscription.granted_by_user.full_name
                        }}</b-link
                      >
                    </template>
                  </i18n>
                  {{
                    communityUser.current_granted_subscription.reason
                      ? $t("components.community.community_user_details.subscription.reason", {
                          reason: communityUser.current_granted_subscription.reason,
                        })
                      : ""
                  }}
                </span>
                <span v-else>
                  {{ $t("components.community.community_user_details.subscription.none") }}
                </span>

                <b-collapse
                  :id="`subscription-${communityUser.id}`"
                  v-model="subscriptionSectionVisible"
                >
                  <div class="font-weight-bold mt-2">
                    {{
                      communityUser.current_granted_subscription
                        ? $t("components.community.community_user_details.subscription.modify")
                        : $t("components.community.community_user_details.subscription.grant")
                    }}
                  </div>
                  <div>
                    <b-form-group
                      class="mb-2"
                      size="sm"
                      :label="
                        $t(
                          'components.community.community_user_details.subscription.reason_input.label'
                        )
                      "
                    >
                      <b-input
                        v-model="grantReason"
                        size="sm"
                        :placeholder="
                          $t(
                            'components.community.community_user_details.subscription.reason_input.placeholder'
                          )
                        "
                      />
                    </b-form-group>
                  </div>
                  <div class="user-details-buttons">
                    <icon-button
                      v-if="subscriptionExpiresInLessThanAMonth"
                      variant="ghost-secondary"
                      size="sm"
                      :disabled="disabled"
                      :onclick="grantMonthSubscription"
                    >
                      {{
                        $t(
                          "components.community.community_user_details.subscription.actions.grant_month"
                        )
                      }}
                    </icon-button>
                    <icon-button
                      v-if="subscriptionExpiresInLessThanAYear"
                      variant="ghost-secondary"
                      size="sm"
                      :disabled="disabled"
                      :onclick="grantYearSubscription"
                    >
                      {{
                        $t(
                          "components.community.community_user_details.subscription.actions.grant_year"
                        )
                      }}
                    </icon-button>
                    <icon-button
                      v-if="communityUser.current_granted_subscription"
                      size="sm"
                      :disabled="disabled"
                      variant="ghost-danger"
                      :onclick="revokeSubscription"
                    >
                      {{
                        $t(
                          "components.community.community_user_details.subscription.actions.revoke"
                        )
                      }}
                    </icon-button>
                  </div>
                </b-collapse>
              </dd>
            </dl>
          </div>
        </div>

        <div class="form__section">
          <h3>
            {{ $t("components.community.community_user_details.tags.title") }}
          </h3>

          <b-form-group
            class="mb-0"
            :label="$t('components.community.community_user_details.tags.member_label')"
            :description="$t('components.community.community_user_details.tags.description')"
          >
            <b-form-select
              :value="volunteerTagValue"
              :options="tagOptions"
              :disabled="disabled"
              @change="updateVolunteerTag"
            />
          </b-form-group>
        </div>

        <div v-if="userRoles.length > 0" class="form__section">
          <h3>
            {{ $t("views.admin.community.roles.title") | capitalize }}
          </h3>

          <i18n path="components.community.community_user_details.roles.management.text" tag="p">
            <template #link_text>
              <a href="#roles">{{
                $t("components.community.community_user_details.roles.management.link_text")
              }}</a>
            </template>
          </i18n>

          <b-badge v-for="role of userRoles" :key="role" :variant="userRoleBadgeVariant(role)">{{
            $t(`communities.fields.user_roles.role.options.${role}`) | capitalize
          }}</b-badge>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<style lang="scss">
.community-user-details {
  .b-icon.bi.info-icon {
    position: absolute;
    right: 0;
    // Larger size and padding so it's a better tap target on mobile
    width: 1.5em;
    height: 2em;
    padding: 0 0.25em 1em;
    vertical-align: middle;
  }

  .collapse-icon {
    transition: 0.3s;

    &.collapse-icon-open {
      transform: rotate(90deg);
    }
  }
}
</style>
